import { create } from 'zustand';
import type { ConnectedSource } from '../types/platforms';
import { CONNECTED_SOURCES } from '../data/platforms';

interface UploadedFile {
  name: string;
  size: number;
  type: string;
}

interface AuditState {
  // Project info
  projectName: string;
  projectDescription: string;
  projectUrl: string;
  projectEmail: string;
  projectLogoPreview: string | null;
  productTypes: string[];
  businessModel: string;
  revenueModels: string[];
  setProjectField: (field: 'projectName' | 'projectDescription' | 'projectUrl' | 'projectEmail', value: string) => void;
  setProjectLogoPreview: (url: string | null) => void;
  toggleProductType: (type: string) => void;
  setBusinessModel: (model: string) => void;
  toggleRevenueModel: (model: string) => void;

  // Connect
  connectedSources: ConnectedSource[];
  uploadedFiles: UploadedFile[];
  selectedIndustry: string;
  chatId: string | null;
  /** The chat the Collect pass ran in. Deliberately separate from `chatId`:
   *  collection and the audit are different runs, and the collected data
   *  reaches the audit as a context parameter (`_source_chat_id`), not by
   *  sharing a chat. Sharing one made the collection pass finalise the audit's
   *  lifecycle and the workflow report an audit that had never started. */
  collectChatId: string | null;
  connectPlatform: (platformId: string) => void;
  disconnectPlatform: (platformId: string) => void;
  addFile: (file: UploadedFile) => void;
  removeFile: (index: number) => void;
  setIndustry: (industry: string) => void;
  setChatId: (chatId: string) => void;
  setCollectChatId: (chatId: string | null) => void;
  reset: () => void;
}

export const useAuditStore = create<AuditState>((set) => ({
  // Project info
  projectName: '',
  projectDescription: '',
  projectUrl: '',
  projectEmail: '',
  projectLogoPreview: null,
  productTypes: [],
  businessModel: '',
  revenueModels: [],

  setProjectField: (field, value) => set({ [field]: value }),
  setProjectLogoPreview: (url) => set({ projectLogoPreview: url }),
  toggleProductType: (type) =>
    set((state) => ({
      productTypes: state.productTypes.includes(type)
        ? state.productTypes.filter((t) => t !== type)
        : [...state.productTypes, type],
    })),
  setBusinessModel: (model) => set({ businessModel: model }),
  toggleRevenueModel: (model) =>
    set((state) => ({
      revenueModels: state.revenueModels.includes(model)
        ? state.revenueModels.filter((m) => m !== model)
        : [...state.revenueModels, model],
    })),

  // Connect
  connectedSources: CONNECTED_SOURCES.map((s) => ({ ...s })),
  uploadedFiles: [],
  selectedIndustry: 'all',
  chatId: null,
  collectChatId: null,

  connectPlatform: (platformId) =>
    set((state) => ({
      connectedSources: state.connectedSources.map((s) =>
        s.id === platformId
          ? { ...s, status: 'connected' as const, accountName: null }
          : s
      ),
    })),

  disconnectPlatform: (platformId) =>
    set((state) => ({
      connectedSources: state.connectedSources.map((s) =>
        s.id === platformId ? { ...s, status: 'disconnected' as const, accountName: null } : s
      ),
    })),

  addFile: (file) =>
    set((state) => ({ uploadedFiles: [...state.uploadedFiles, file] })),

  removeFile: (index) =>
    set((state) => ({
      uploadedFiles: state.uploadedFiles.filter((_, i) => i !== index),
    })),

  setIndustry: (industry) => set({ selectedIndustry: industry }),

  setChatId: (chatId) => set({ chatId }),

  setCollectChatId: (collectChatId) => set({ collectChatId }),

  reset: () =>
    set({
      projectName: '',
      projectDescription: '',
      projectUrl: '',
      projectEmail: '',
      projectLogoPreview: null,
      productTypes: [],
      businessModel: '',
      revenueModels: [],
      connectedSources: CONNECTED_SOURCES.map((s) => ({ ...s })),
      uploadedFiles: [],
      selectedIndustry: 'all',
      chatId: null,
      collectChatId: null,
    }),
}));
